// CustomerSearch — debounced (300ms) type-ahead against
// GET /api/v1/customers?search=. Selecting a result fires onSelect with
// the full CustomerSummary; the parent form populates name/phone/etc.
//
// "New customer" toggle inverts the panel: typing is hidden, the parent
// form clears its customer_id link (so the booking will carry a fresh
// snapshot), and a small "Search instead" button restores the search.
//
// Per CLIENT-DECISION-010 the parent owns form state — this component
// is a pure controlled lookup that emits selection events.

import { useEffect, useRef, useState } from 'react';
import { Search as SearchIcon, UserPlus, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '../../../components/form/Input';
import { Button } from '../../../components/ui/Button/Button';
import { useDebounce } from '../../../hooks/useDebounce';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { CustomerListResponse, CustomerSummary } from '../../../types/api';
import styles from './CustomerSearch.module.css';

// Brief: 300ms debounce, ≥2 chars, return up to 10 matches per request.
const MIN_QUERY = 2;
const PAGE_SIZE = 10;

export interface CustomerSearchProps {
  /** When true, show the "Search again" UI rather than the search input. */
  newCustomerMode: boolean;
  onSelect: (customer: CustomerSummary) => void;
  onToggleNewCustomer: (newMode: boolean) => void;
}

async function fetchCustomers(search: string): Promise<CustomerSummary[]> {
  const res = await apiClient.get<CustomerListResponse>(endpoints.customers.list, {
    params: { search, limit: PAGE_SIZE },
  });
  return res.data?.customers ?? [];
}

export function CustomerSearch({
  newCustomerMode,
  onSelect,
  onToggleNewCustomer,
}: CustomerSearchProps): JSX.Element {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounce(term, 300);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const enabled = !newCustomerMode && debounced.trim().length >= MIN_QUERY;
  const query = useQuery({
    queryKey: queryKeys.customers.list({ search: debounced }),
    queryFn: () => fetchCustomers(debounced.trim()),
    enabled,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;
    function handleAway(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleAway);
    return () => document.removeEventListener('mousedown', handleAway);
  }, [open]);

  if (newCustomerMode) {
    return (
      <div className={styles.wrap}>
        <div className={styles.newCustomer}>
          <span className={styles.newCustomerLabel}>
            <UserPlus size={16} aria-hidden /> New customer — fields below
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onToggleNewCustomer(false);
              setTerm('');
            }}
          >
            Search instead
          </Button>
        </div>
      </div>
    );
  }

  const results = query.data ?? [];
  const showResults = open && enabled;

  return (
    <div className={styles.wrap} ref={wrapperRef}>
      <div className={styles.searchRow}>
        <div className={styles.searchInputWrap}>
          <SearchIcon size={16} aria-hidden className={styles.searchIcon} />
          <Input
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search customers by name, phone, or email…"
            aria-label="Customer search"
            autoComplete="off"
            className={styles.searchInput}
          />
          {term ? (
            <button
              type="button"
              className={styles.clear}
              aria-label="Clear search"
              onClick={() => {
                setTerm('');
                setOpen(false);
              }}
            >
              <X size={14} aria-hidden />
            </button>
          ) : null}
        </div>
        <Button
          variant="secondary"
          size="md"
          icon={UserPlus}
          onClick={() => onToggleNewCustomer(true)}
        >
          New customer
        </Button>
      </div>

      {showResults ? (
        <div role="listbox" className={styles.results}>
          {query.isLoading ? (
            <div className={styles.empty}>Searching…</div>
          ) : query.isError ? (
            <div className={styles.empty}>Could not search customers.</div>
          ) : results.length === 0 ? (
            <div className={styles.empty}>
              No customers match. Use <strong>New customer</strong> to add one.
            </div>
          ) : (
            results.map((c) => (
              <button
                type="button"
                role="option"
                aria-selected="false"
                key={c.uuid}
                className={styles.result}
                onClick={() => {
                  onSelect(c);
                  setOpen(false);
                  setTerm('');
                }}
              >
                <span className={styles.resultName}>{c.full_name}</span>
                <span className={styles.resultMeta}>
                  {c.phone}
                  {c.email ? ` · ${c.email}` : ''}
                </span>
                <span className={styles.resultPostcode}>{c.postcode}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
